import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {GenericModalComponent} from '@shared/components/generic-modal/generic-modal.component';
import XLSX from 'xlsx';
import {ThousandsCommaPipe} from '@shared/pipes/thousands-comma.pipe';

export interface IExcelTemplate {
    colName: string;
    field?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ExcelUtilsService {
    constructor(
        private dialog: MatDialog,
    ) {
    }

    private convertHtmlToString(html: string): string {
        const div = document.createElement('div');
        if (html) {
            div.innerHTML = html;
        }
        return div.textContent || div.innerText || '';
    }

    private escapeExcelTokens(text: string): string {
        return text.replace(/_x0020_/g, '_x005F_x0020_');
    }

    export(excelName, template: IExcelTemplate[], data?: any[], wsName?, quantity?) {
        const thousandsPipe = new ThousandsCommaPipe();
        const total = quantity > 50000 ? thousandsPipe.transform(50000) : thousandsPipe.transform(quantity);
        const dialogRef = this.dialog.open(GenericModalComponent, {
            width: '430px',
            height: '250px',
            disableClose: true,
            backdropClass: 'backdropBackground',
            data: {
                enableClose: false,
                headerIcon: {
                    name: 'assets/app/img/loader/octopus.gif',
                    width: '90px',
                },
                title: `Exporting ${total} Assets`,
                subTitle: '* Export is limited to 50K results. Use search and filter to export relevant results',
                content: 'Almost there...',
            },
        });

        dialogRef.afterOpened()
            .subscribe(() => {
                const templateColNames = template.map(a => a.colName);
                const templateFieldNames = template.map(a => a.field);
                const excelArr = [templateColNames];
                data?.forEach(a => {
                    const dataArr = templateFieldNames.map(b => {
                        const fieldValue = b === 'tOOL_NAME' ? (a[b] || a['tOOL_TYPE']) : a[b];
                        return typeof fieldValue === 'string'
                            ? this.escapeExcelTokens(this.convertHtmlToString(fieldValue))
                            : fieldValue;
                    });
                    excelArr.push(dataArr);
                });
                const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(excelArr);
                const wb: XLSX.WorkBook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, wsName);
                XLSX.writeFile(wb, `${excelName}.xlsx`);
                dialogRef.componentInstance.data.enableClose = true;
                dialogRef.componentInstance.data.title = 'Export Complete';
                dialogRef.componentInstance.data.subTitle = 'Your file has been successfully downloaded.';
                dialogRef.componentInstance.data.content = 'The CSV file is ready for use. Click "Close" to exit.';
                dialogRef.componentInstance.data.headerIcon = {
                    name: 'assets/app/img/icons/excel_green.png',
                    width: '65px',
                };
            });
    }

    export2(config, template, data) {
        const thousandsPipe = new ThousandsCommaPipe();
        if (config.quantity) {
            const total = config.quantity > 50000 ? thousandsPipe.transform(50000) : thousandsPipe.transform(config.quantity);
        }
        const dialogRef = this.dialog.open(GenericModalComponent, {
            width: '430px',
            height: config.height,
            disableClose: true,
            backdropClass: 'backdropBackground',
            data: {
                enableClose: false,
                headerIcon: {
                    name: 'assets/app/img/loader/octopus.gif',
                    width: '90px',
                },
                title: config.title || '',
                subTitle: config.subTitle || '',
                content: config.content || '',
            },
        });

        dialogRef.afterOpened()
            .subscribe(() => {
                const templateColNames = template.map(a => a.colName);
                const templateFieldNames = template.map(a => a.field);
                const excelArr = [templateColNames];
                data?.forEach(a => {
                    const dataArr = templateFieldNames.map(b => {
                        const val = a[b];
                        return typeof val === 'string' ? this.escapeExcelTokens(val) : val;
                    });
                    excelArr.push(dataArr);
                });
                const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(excelArr);
                const wb: XLSX.WorkBook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, config.wsName || '');
                XLSX.writeFile(wb, `${config.excelName || 'temp'}.xlsx`);
                dialogRef.close();
            });
    }

    export3(excelName, template: IExcelTemplate[], data?: any[], wsName?, quantity?) {
        const templateColNames = template.map(a => a.colName);
        const templateFieldNames = template.map(a => a.field);
        const excelArr = [templateColNames];
        data?.forEach(a => {
            const dataArr = templateFieldNames.map(b => {
                const val = a[b];
                return typeof val === 'string' ? this.escapeExcelTokens(val) : val;
            });
            excelArr.push(dataArr);
        });
        const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(excelArr);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, wsName);
        XLSX.writeFile(wb, `${excelName}.xlsx`);
    }

    async multipleDataExport(excelName: string, templates: Array<{ data: Array<any>, template: Array<IExcelTemplate> }>, wsName?: string) {
        const excelArr = [];
        templates.forEach((template, index) => {
            const templateColNames = template.template.map(a => a.colName);
            const templateFieldNames = template.template.map(a => a.field);
            excelArr.push(templateColNames);
            template.data.forEach(a => {
                const dataArr = templateFieldNames.map(b => {
                    const fieldValue = b === 'tOOL_NAME' ? (a[b] || a['tOOL_TYPE']) : a[b];
                    return typeof fieldValue === 'string'
                        ? this.escapeExcelTokens(this.convertHtmlToString(fieldValue))
                        : fieldValue;
                });
                excelArr.push(dataArr);
            });
            excelArr.push([]);
            excelArr.push([]);
        });
        const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(excelArr);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, wsName);
        XLSX.writeFile(wb, `${excelName}.xlsx`);
    }
}
